<script lang="tsx">
  import { set } from 'lodash-es';
  import { computed, defineComponent, ref, toRaw, unref, watchEffect } from 'vue';
  import { useRoute } from 'vue-router';
  import type { PropType } from 'vue';
  import {
    isArray,
    isBoolean,
    isFunction,
    isNumber,
    isString,
    propTypes,
    replaceUrl,
  } from '@mts-fe/utils';
  import { createPlaceholderMessage } from '@mts/components/Form/src/helper';
  import { useTableContext } from '../../hooks/useTableContext';
  import type { BasicColumn } from '../../types/table';
  import EditComponent from './editComp';
  import formatCell from './formatCell';
  import RenderCell from './RenderCell.vue';

  export default defineComponent({
    name: 'EditableCell',
    components: { EditComponent },
    props: {
      value: {
        type: [String, Number, Boolean, Object] as PropType<
          string | number | boolean | Record<string, any> | any[]
        >,
      },
      record: {
        type: Object as any,
      },
      column: {
        type: Object as PropType<BasicColumn>,
        default: () => ({}),
      },
      index: propTypes.number,
      setValidFn: {
        type: Function as PropType<(fn: () => Promise<boolean>) => void>,
      },
    },
    setup(props) {
      const table = useTableContext();
      const ruleMessage = ref<string>();
      const currentValueRef = ref<any>(props.value);

      watchEffect(() => {
        currentValueRef.value = props.value;
      });

      const getComponent = computed(() => props.column.editComponent || 'Input');
      const hasRule = computed(() => props.column.editRule || props.column.required);

      const getDisabled = computed(() => {
        const { editDynamicDisabled } = props.column;
        if (isFunction(editDynamicDisabled)) {
          const { record, column, index } = props;
          return editDynamicDisabled({ text: currentValueRef.value, record, column, index });
        }
        return editDynamicDisabled;
      });

      const getComponentProps = computed(() => {
        let compProps = props.column?.editComponentProps ?? ({} as any);

        const isCheckValue = ['Checkbox', 'Switch'].includes(getComponent.value);
        const valueField = isCheckValue ? 'checked' : 'value';
        const val = unref(currentValueRef);
        let value = val;
        const { checkedValue, unCheckedValue } = compProps;
        if (isCheckValue) {
          if (typeof checkedValue !== 'undefined') {
            value = val === checkedValue ? checkedValue : unCheckedValue;
          } else if (typeof unCheckedValue !== 'undefined') {
            value = val === unCheckedValue ? unCheckedValue : checkedValue;
          } else {
            value = isNumber(val) || isBoolean(val) ? val : !!val;
          }
        }

        const { record, column, index } = props;

        if (isFunction(compProps)) {
          compProps = compProps({ text: val, record, column, index }) ?? {};
        }

        ((attrs: string[]) => {
          attrs.forEach((key) => {
            if (compProps[key] && typeof compProps[key] === 'string') {
              compProps[key] = replaceUrl(compProps[key], record);
            }
          });
        })(['prefix', 'suffix', 'suffixIcon', 'addonBefore', 'addonAfter']);

        if (getComponent.value.includes('Picker') && !compProps.valueFormat) {
          compProps.valueFormat = compProps.format;
        }

        // 用临时变量存储 onChange方法 用于 handleChange方法 获取，并删除原始onChange, 防止存在两个 onChange
        compProps.onChangeTemp = compProps.onChange;
        delete compProps.onChange;

        upEditDynamicDisabled(record, column, value);
        return {
          allowClear: true,
          placeholder: !unref(getDisabled) ? createPlaceholderMessage(unref(getComponent)) : void 0,
          getPopupContainer: () => unref(table?.wrapRef.value) ?? document.body,
          ...compProps,
          [valueField]: value,
          disabled: unref(getDisabled),
        } as any;
      });
      function upEditDynamicDisabled(record, column, value) {
        if (!record) return false;
        const { key, dataIndex } = column;
        if (!key && !dataIndex) return;
        const dataKey = (dataIndex || key) as string;
        set(record, dataKey, value);
      }

      async function handleChange(e: any, ...rest: any[]) {
        const component = unref(getComponent);
        if (!e) {
          currentValueRef.value = e;
        } else if (component === 'Checkbox') {
          currentValueRef.value = e.target.checked;
        } else if (component === 'Switch') {
          currentValueRef.value = e;
        } else if (e?.target && Reflect.has(e.target, 'value')) {
          currentValueRef.value = e.target.value;
        } else if (isString(e) || isBoolean(e) || isNumber(e) || isArray(e)) {
          currentValueRef.value = e;
        }
        const onChange = unref(getComponentProps)?.onChangeTemp;
        if (onChange && isFunction(onChange)) onChange(e, ...rest);

        const record = toRaw(props.record);
        const { rowKey } = table.getBindValues.value;
        !record.key && !rowKey && console.warn('key不存在', record, table.getBindValues.value);
        table.updateTableDataRecord(record.key || record[rowKey as string], record);

        table.emit?.('edit-change', {
          value: unref(currentValueRef),
          record,
          column: props.column,
          index: props.index,
        });
        editValidate();
      }

      async function editValidate() {
        const { editRule = props.column.required } = props.column;
        const currentValue = unref(currentValueRef);

        if (editRule) {
          if (isBoolean(editRule) && !currentValue) {
            ruleMessage.value = createPlaceholderMessage(getComponent.value);
            return false;
          }
          if (isFunction(editRule)) {
            const res = await editRule({ ...props, text: currentValue });
            if (res) {
              ruleMessage.value = res;
              return false;
            } else {
              ruleMessage.value = '';
              return true;
            }
          }
        }
        ruleMessage.value = '';
        return true;
      }
      hasRule.value && props.setValidFn?.(editValidate);

      const route = useRoute();
      const isShowEdit = computed(() => {
        const { record, column, index } = props;
        const { ifEditShow = true } = props.column;
        if (ifEditShow && isFunction(ifEditShow)) {
          return ifEditShow({ text: unref(currentValueRef), record, column, index, route });
        }
        return ifEditShow;
      });

      return {
        isShowEdit,
        currentValueRef,
        handleChange,
        getComponent,
        hasRule,
        ruleMessage,
        getComponentProps,
        getDisabled,
      };
    },
    render() {
      return this.isShowEdit ? (
        <EditComponent
          status={this.hasRule && this.ruleMessage ? 'error' : ''}
          {...this.getComponentProps}
          component={this.getComponent}
          hasRule={this.hasRule}
          ruleMessage={this.ruleMessage}
          onChange={this.handleChange}
        />
      ) : this.column.render ? (
        <RenderCell {...this.$props} text={this.currentValueRef} />
      ) : (
        formatCell(this.currentValueRef, this.column.format, this.record, this.index)
      );
    },
  });
</script>
